import { z } from 'zod'
import type { ErrorKey } from './i18n.js'

const SquareSchema = z.string().regex(/^[a-h][1-8]$/)

/**
 * Commands sent by a player (human or bot). The server adds the player's
 * identity: it is never provided by the client.
 */
export const PlayerCommandSchema = z.discriminatedUnion('type', [
  /** Former Joker: chosen number of moves (1..maxCardNumber); kept for games in progress. */
  z.object({ type: z.literal('chooseEffect'), moves: z.number().int().min(1).max(9) }),
  /** Placement of a recovered pawn (+2 / +4) on an allowed square. */
  z.object({ type: z.literal('placePawn'), square: SquareSchema }),
  z.object({
    type: z.literal('move'),
    from: SquareSchema,
    to: SquareSchema,
    promotion: z.enum(['q', 'r', 'b', 'n']).optional(),
  }),
  z.object({ type: z.literal('endSeries') }),
  z.object({ type: z.literal('offerDraw') }),
  z.object({ type: z.literal('acceptDraw') }),
  z.object({ type: z.literal('declineDraw') }),
  z.object({ type: z.literal('resign') }),
])

export type PlayerCommand = z.infer<typeof PlayerCommandSchema>

/** Commands issued by the server only (never accepted from a client). */
/** `idle`: the player did not act in time (tournament games). */
export type ServerCommand = { type: 'forfeit'; reason: 'disconnect' | 'idle' }

export type GameCommand = PlayerCommand | ServerCommand

export const CommandEnvelopeSchema = z.object({
  gameId: z.string().min(1).max(64),
  /** Unique command id: a repetition never has a second effect. */
  commandId: z.string().min(8).max(64),
  /** State version expected by the client; a stale command is rejected. */
  expectedVersion: z.number().int().min(0),
  command: PlayerCommandSchema,
})

export type CommandEnvelope = z.infer<typeof CommandEnvelopeSchema>

export type CommandErrorCode =
  | 'not_your_turn'
  | 'wrong_phase'
  | 'invalid_move'
  | 'invalid_square'
  | 'invalid_input'
  | 'game_over'
  | 'stale_version'
  | 'not_in_game'
  | 'rate_limited'
  | 'unauthorized'
  | 'not_found'
  | 'internal'

export interface CommandError {
  code: CommandErrorCode
  /** French text (fallback). */
  message: string
  /** Message id, rendered in the client's language (see i18n.ts). */
  key?: ErrorKey
  params?: Record<string, string | number>
}
